import { useState, useRef, useEffect } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';

const categoryImages: Record<string, string> = {
  'Shoes': 'https://readdy.ai/api/search-image?query=Single%20pair%20of%20white%20minimalist%20sneakers%20on%20natural%20linen%20fabric%20with%20soft%20shadow%20warm%20daylight%20clean%20product%20photography%20against%20off%20white%20background%20simple%20elegant%20composition&width=200&height=150&seq=cat-shoes-img&orientation=landscape',
  'Hoodies / Sweaters': 'https://readdy.ai/api/search-image?query=Beige%20oversized%20hoodie%20neatly%20folded%20on%20light%20wooden%20surface%20with%20soft%20natural%20shadows%20warm%20toned%20lifestyle%20photography%20minimalist%20clothing%20display%20clean%20aesthetic&width=200&height=150&seq=cat-hoodies-img&orientation=landscape',
  'T-Shirts': 'https://readdy.ai/api/search-image?query=White%20cotton%20tshirt%20flat%20lay%20on%20cream%20linen%20texture%20fabric%20soft%20natural%20daylight%20clean%20minimalist%20fashion%20photography%20with%20gentle%20wrinkles%20and%20subtle%20shadows&width=200&height=150&seq=cat-tshirts-img&orientation=landscape',
  'Jackets': 'https://readdy.ai/api/search-image?query=Olive%20green%20utility%20jacket%20hanging%20on%20wooden%20hanger%20against%20warm%20cream%20wall%20soft%20natural%20light%20stylish%20minimalist%20fashion%20display%20editorial%20aesthetic&width=200&height=150&seq=cat-jackets-img&orientation=landscape',
  'Pants / Shorts': 'https://readdy.ai/api/search-image?query=Beige%20linen%20trousers%20casually%20draped%20over%20wooden%20chair%20near%20sunlit%20window%20warm%20natural%20lighting%20soft%20shadows%20relaxed%20lifestyle%20fashion%20photography%20minimalist%20aesthetic&width=200&height=150&seq=cat-pants-img&orientation=landscape',
  'Headwear': 'https://readdy.ai/api/search-image?query=Neutral%20tone%20baseball%20cap%20and%20beanie%20hat%20displayed%20on%20wooden%20surface%20with%20soft%20diffused%20light%20warm%20beige%20background%20minimal%20accessory%20styling%20clean%20product%20photography&width=200&height=150&seq=cat-headwear-img&orientation=landscape',
  'Sets': 'https://readdy.ai/api/search-image?query=Matching%20cream%20sweatshirt%20and%20sweatpants%20set%20folded%20together%20on%20light%20oak%20surface%20soft%20morning%20light%20coordinated%20fashion%20loungewear%20display%20warm%20neutral%20aesthetic&width=200&height=150&seq=cat-sets-img&orientation=landscape',
  'Underwear / Underpants': 'https://readdy.ai/api/search-image?query=Cotton%20underwear%20neatly%20arranged%20on%20white%20linen%20fabric%20soft%20natural%20light%20clean%20minimalist%20product%20display%20subtle%20warm%20tones%20fresh%20aesthetic&width=200&height=150&seq=cat-underwear-img&orientation=landscape',
  'Jersey': 'https://readdy.ai/api/search-image?query=Sport%20jersey%20fabric%20texture%20closeup%20on%20light%20surface%20with%20soft%20natural%20lighting%20athletic%20wear%20material%20detail%20clean%20minimalist%20sports%20aesthetic%20warm%20neutral%20background&width=200&height=150&seq=cat-jersey-img&orientation=landscape',
  'Accessories': 'https://readdy.ai/api/search-image?query=Minimalist%20gold%20jewelry%20and%20sunglasses%20arranged%20on%20warm%20beige%20linen%20fabric%20soft%20natural%20light%20elegant%20accessory%20flatlay%20clean%20aesthetic%20with%20gentle%20shadows&width=200&height=150&seq=cat-accessories-img&orientation=landscape',
  'Bags': 'https://readdy.ai/api/search-image?query=Tan%20leather%20tote%20bag%20standing%20on%20light%20wooden%20table%20near%20window%20soft%20natural%20daylight%20minimalist%20bag%20display%20warm%20earth%20tones%20clean%20product%20photography&width=200&height=150&seq=cat-bags-img&orientation=landscape',
  'Belts': 'https://readdy.ai/api/search-image?query=Brown%20leather%20belt%20coiled%20elegantly%20on%20cream%20linen%20surface%20soft%20warm%20light%20natural%20texture%20detail%20minimalist%20accessory%20photography%20clean%20aesthetic&width=200&height=150&seq=cat-belts-img&orientation=landscape',
  'Perfumes': 'https://readdy.ai/api/search-image?query=Amber%20glass%20perfume%20bottle%20on%20light%20marble%20surface%20with%20soft%20natural%20light%20elegant%20minimalist%20fragrance%20display%20warm%20tones%20clean%20sophisticated%20product%20photography&width=200&height=150&seq=cat-perfumes-img&orientation=landscape',
  'Other Stuff': 'https://readdy.ai/api/search-image?query=Assorted%20small%20lifestyle%20accessories%20including%20notebook%20candle%20and%20ceramic%20cup%20on%20wooden%20tray%20soft%20warm%20light%20cozy%20minimalist%20flatlay%20neutral%20earthy%20tones%20clean%20aesthetic&width=200&height=150&seq=cat-other-img&orientation=landscape',
};

export default function CategorySection() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (url: string) => {
    setIsDropdownOpen(false);
    window.open(url, '_blank', 'noopener,nofollow');
  };

  return (
    <section id="categories" className="py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <p className="text-sm font-semibold text-primary-500 uppercase tracking-wider mb-3">Kategorie</p>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
            Przeglądaj kategorie produktów
          </h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
            Wybierz interesującą Cię kategorię i przejdź bezpośrednio do arkusza produktów na Kakobuy Spreadsheet
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-sm shadow-sm hover:shadow-md"
          >
            <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
            Kakobuy Spreadsheet
          </a>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-foreground-900 hover:bg-foreground-800 text-background-50 font-semibold rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-sm shadow-sm hover:shadow-md"
          >
            <i className="ri-shopping-cart-line w-4 h-4 flex items-center justify-center"></i>
            Buy It Now
          </a>
        </div>

        <div className="relative max-w-md mx-auto mb-12" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full flex items-center justify-between gap-3 px-5 py-3.5 bg-background-100 border-2 border-background-200 hover:border-primary-300 rounded-xl text-sm font-medium text-foreground-700 transition-all duration-200 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-menu-search-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
              Wybierz kategorię produktów...
            </span>
            <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center text-foreground-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {isDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 border border-background-200 rounded-xl shadow-lg z-40 max-h-80 overflow-y-auto animate-scale-in">
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleCategorySelect(cat.url)}
                  className="w-full flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors text-left cursor-pointer first:rounded-t-xl last:rounded-b-xl"
                >
                  <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-foreground-400`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {categories.map((cat, index) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col items-center bg-background-100 hover:bg-primary-50 rounded-xl border border-background-200 hover:border-primary-200 transition-all duration-200 cursor-pointer overflow-hidden"
              style={{
                animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`,
              }}
            >
              <div className="w-full aspect-[4/3] overflow-hidden">
                <img
                  src={categoryImages[cat.name] || categoryImages['Other Stuff']}
                  alt={`Kategoria ${cat.name}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="flex flex-col items-center gap-1.5 p-3 w-full">
                <div className="w-8 h-8 flex items-center justify-center bg-background-50 rounded-lg group-hover:bg-primary-100 transition-colors">
                  <i className={`${cat.icon} text-sm text-foreground-500 group-hover:text-primary-500 transition-colors`}></i>
                </div>
                <span className="text-xs font-medium text-foreground-600 group-hover:text-primary-600 transition-colors text-center leading-tight">
                  {cat.name}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}